import React, { FC, Fragment, useCallback, useMemo, useState } from 'react';
import { useDebounceValue, useWindowSize } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';
import * as S from './PremiumAdsGrid.styles';
import { DEFAULT_ROWS_COUNT_BEFORE_BANNER } from './PremiumAdsGrid.constants';
import { getCardsCountInRow } from './PremiumAdsGrid.helpers';
import { Ad } from '@/api/types/common.types';
import { AdCardNew } from '@/components/AdCardNew';
import { useToastStore } from '@/store/useToastStore';
import { generateRandomNumber } from '@/utils/helpers';
import { useBanner } from '@/hooks/useBanner';
import { CollectionMasterModal, CollectionMasterSheet } from '@/features';
import { Toast } from '@/components/UI/Toast';
import { useResponsive } from '@/hooks/useResponsive';
import { useAuthStore } from '@/store/useAuthStore';
import { AddToPremiumCard } from '@/components/AdsWithInfiniteScroll/PremiumAdsWithInfiniteScroll/AddToPremiumCard';
import { AdsChunkSize } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.types';
import { TransitionSourceBlockEnum } from '@/api/types/ads.types';
import { useViewedAds } from '@/hooks/useViewedAds';
import { useIosNudgeScrollOffTop } from '@/hooks/useIosNudgeScrollOffTop';

interface PremiumAdsGridProps {
  ads: Ad[] | undefined;
  isLoading: boolean;
}

export const PremiumAdsGrid: FC<PremiumAdsGridProps> = ({ ads, isLoading }) => {
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const [debouncedWidth] = useDebounceValue(width, 200);
  const { openToast, closeToast, onAnimationComplete } = useToastStore();
  const { inListBanner, bottomBanner } = useBanner();
  const [isCollectionMasterOpen, setIsCollectionMasterOpen] = useState(false);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const { isMobile, isDesktop } = useResponsive();
  const { canShowViewedState, viewedIds } = useViewedAds();
  const iosNudgeScrollOffTop = useIosNudgeScrollOffTop();

  const handleAddToFavorites = (targetAd: Ad) => {
    closeToast();
    onAnimationComplete();

    setTimeout(() => {
      openToast({
        image: targetAd?.photo?.url,
        text: t('bookmark_collections.toast_tip'),
        action: t('bookmark_collections.toast_action'),
        onActionClick: handleToastActionClick,
      });
    }, 50);
  };

  const handleRemoveFromFavorites = (targetAd: Ad) => {
    closeToast();
    onAnimationComplete();

    setTimeout(() => {
      openToast({
        image: targetAd?.photo?.url,
        text: t('bookmark_collections.unbookmarked'),
      });
    }, 50);
  };

  const handleToastActionClick = () => {
    closeToast();
    iosNudgeScrollOffTop();
    setIsCollectionMasterOpen(true);
  };

  const handleCloseCollectionMaster = () => {
    setIsCollectionMasterOpen(false);
  };

  const getBannerPosition = useCallback(
    (idx: number): { isBannerRenders: boolean; isFirstBanner: boolean } => {
      const cardsCountInRow = getCardsCountInRow(debouncedWidth);
      const isBannerPosition =
        idx % (DEFAULT_ROWS_COUNT_BEFORE_BANNER * cardsCountInRow) === 0;
      return {
        isBannerRenders:
          idx !== 0 && isBannerPosition && !!inListBanner && isMobile,
        isFirstBanner:
          idx === DEFAULT_ROWS_COUNT_BEFORE_BANNER * cardsCountInRow,
      };
    },
    [debouncedWidth, ads, inListBanner, isMobile]
  );

  const renderedAds = useMemo(() => {
    return ads?.map((item, idx) => {
      const key = `${item.id}_${idx}`;
      const adCard = (
        <AdCardNew
          key={key}
          ad={item}
          onAddBookmark={handleAddToFavorites}
          onRemoveBookmark={handleRemoveFromFavorites}
          priority={!idx}
          transitionSource={TransitionSourceBlockEnum.Premium}
          canShowViewedState={canShowViewedState}
          viewedFromStorage={viewedIds.has(String(item.id))}
        />
      );

      const { isBannerRenders, isFirstBanner } = getBannerPosition(idx);

      if (isBannerRenders) {
        const banner = (
          <S.BannerBox>
            {isFirstBanner
              ? bottomBanner
              : inListBanner?.(generateRandomNumber())}
          </S.BannerBox>
        );
        return (
          <Fragment key={`${key}_fragment`}>
            {banner}
            {adCard}
          </Fragment>
        );
      }

      return adCard;
    });
  }, [
    ads,
    handleAddToFavorites,
    t,
    getBannerPosition,
    inListBanner,
    bottomBanner,
    isMobile,
    canShowViewedState,
    viewedIds,
  ]);

  if (!ads?.length && !isLoading) return null;

  const renderLoader = () => {
    return Array.from({ length: AdsChunkSize.Middle }).map((_, index) => (
      <AdCardNew key={index} />
    ));
  };

  return (
    <>
      <S.Container className="premiumAds">
        {isAuthenticated && isDesktop && <AddToPremiumCard />}
        {!ads && isLoading ? renderLoader() : renderedAds}
      </S.Container>

      {isDesktop ? (
        <CollectionMasterModal
          isOpen={isCollectionMasterOpen}
          onClose={handleCloseCollectionMaster}
        />
      ) : (
        <CollectionMasterSheet
          isOpen={isCollectionMasterOpen}
          onClose={handleCloseCollectionMaster}
        />
      )}

      <Toast />
    </>
  );
};

PremiumAdsGrid.displayName = 'PremiumAdsGrid';
